import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useEffect, useState } from 'react';
import classes from './addRowModalDrugReferenceFgNdcToBulkNdc.module.scss';
import { toast } from 'react-toastify';

interface FormData {
  fg_ndc: string;
  bulk_ndc: string;
  description: string;
  primary_ingredient: string;
  supplier: string;
  supplier_name: string;
  dosage_form: string;
  coating: number | null;
  product_color: string;
}

interface DropdownOption {
  label: string;
  value: any;
}

interface AddRowModalDrugReferenceFgNdcToBulkNdcProps {
  visible: boolean;
  onHide: () => void;
  onSubmit: (data: FormData) => Promise<void>;
  initialValues?: FormData | null;
  isEdit: boolean;
  coatingOptions: DropdownOption[];
  dosageFormOptions: DropdownOption[];
}

const AddRowModalDrugReferenceFgNdcToBulkNdc = ({
  visible,
  onHide,
  onSubmit,
  initialValues,
  isEdit,
  coatingOptions,
  dosageFormOptions
}: AddRowModalDrugReferenceFgNdcToBulkNdcProps) => {
  const [formData, setFormData] = useState<FormData>({
    fg_ndc: '',
    bulk_ndc: '',
    description: '',
    primary_ingredient: '',
    supplier: '',
    supplier_name: '',
    dosage_form: '',
    coating: null,
    product_color: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const environment = import.meta.env.VITE_ENVIRONMENT;

  useEffect(() => {
    if (initialValues && visible) {
      setFormData({ ...initialValues });
      setErrors({});
    } else if (!isEdit && visible) {
      // Reset form for new entry
      setFormData({
        fg_ndc: '',
        bulk_ndc: '',
        description: '',
        primary_ingredient: '',
        supplier: '',
        supplier_name: '',
        dosage_form: '',
        coating: null,
        product_color: ''
      });
      setErrors({});
    }
  }, [initialValues, visible, isEdit]);

  // Auto-set coating to N/A when dosage form is "Any"
  useEffect(() => {
    if (formData.dosage_form === 'Any') {
      setFormData(prev => ({ ...prev, coating: null }));
    }
  }, [formData.dosage_form]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Only validate required fields when adding new entry
    if (!isEdit) {
      // Required fields
      if (!formData.fg_ndc) {
        newErrors.fg_ndc = 'Finished Good NDC is required';
      }
      if (!formData.bulk_ndc) {
        newErrors.bulk_ndc = 'Bulk NDC is required';
      }

      // Format validation only for FG NDC
      const ndcFormat = /^\d{5}-\d{3}-\d{2}$/;
      if (
        formData.fg_ndc &&
        formData.bulk_ndc &&
        !ndcFormat.test(formData.fg_ndc) &&
        !ndcFormat.test(formData.bulk_ndc)
      ) {
        newErrors.fg_ndc = 'Finished Good NDC should be in format: #####-###-##';
        newErrors.bulk_ndc = 'Bulk NDC should be in format: #####-###-##';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (validateForm()) {
      try {
        setIsSubmitting(true);
        // When editing, only submit the fields that are editable
        if (isEdit && initialValues) {
          const editedData = {
            ...initialValues,
            dosage_form: formData.dosage_form,
            coating: formData.coating,
            product_color: formData.product_color
          };
          await onSubmit(editedData);
        } else {
          await onSubmit(formData);
        }
        // Only close modal on success
        onHide();
      } catch (error) {
        // Don't close modal on error - error will be shown in toast
        if (environment === 'development') {
          console.error('Error submitting form:', error);
        }
        toast.error('Error submitting form for Drug Reference FG NDC to Bulk NDC data!');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleInputChange = (field: keyof FormData, value: any) => {
    // Add number-only validation for Primary Ingredient and Supplier fields
    if ((field === 'primary_ingredient' || field === 'supplier') && value !== '') {
      // Only allow numbers
      if (!/^\d*$/.test(value)) {
        return; // Don't update if not a number
      }
    }

    // For dropdown fields, ensure we get the value, not the option object
    if (field === 'coating' && value && typeof value === 'object' && 'value' in value) {
      value = value.value;
    }

    setFormData({ ...formData, [field]: value });

    // Clear error when field is updated
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  // Check if coating should be disabled
  const isCoatingDisabled = formData.dosage_form === 'Any';

  return (
    <Dialog
      header={
        isEdit
          ? 'Edit Drug Reference FG NDC to Bulk NDC'
          : 'Add New Drug Reference FG NDC to Bulk NDC'
      }
      visible={visible}
      onHide={onHide}
      className={classes.modal}
      style={{ width: '800px' }}
    >
      <div className={classes.formContainer}>
        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Finished Good NDC</label>
            <InputText
              value={formData.fg_ndc}
              onChange={e => handleInputChange('fg_ndc', e.target.value)}
              placeholder="e.g. 12345-678-90"
              className={`${classes.inputText} ${errors.fg_ndc ? 'p-invalid' : ''}`}
              disabled={isEdit || isSubmitting}
            />
            {errors.fg_ndc && <small className="p-error">{errors.fg_ndc}</small>}
          </div>

          <div className={classes.formGroup}>
            <label>Bulk NDC</label>
            <InputText
              value={formData.bulk_ndc}
              onChange={e => handleInputChange('bulk_ndc', e.target.value)}
              placeholder="Enter Bulk NDC"
              className={`${classes.inputText} ${errors.bulk_ndc ? 'p-invalid' : ''}`}
              disabled={isEdit || isSubmitting}
            />
            {errors.bulk_ndc && <small className="p-error">{errors.bulk_ndc}</small>}
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Description</label>
            <InputText
              value={formData.description}
              onChange={e => handleInputChange('description', e.target.value)}
              placeholder="Enter Description"
              className={classes.inputText}
              disabled={isEdit || isSubmitting}
            />
          </div>

          <div className={classes.formGroup}>
            <label>Primary Ingredient</label>
            <InputText
              value={formData.primary_ingredient}
              onChange={e => handleInputChange('primary_ingredient', e.target.value)}
              placeholder="Enter Primary Ingredient"
              className={classes.inputText}
              disabled={isEdit || isSubmitting}
            />
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Supplier</label>
            <InputText
              value={formData.supplier}
              onChange={e => handleInputChange('supplier', e.target.value)}
              placeholder="Enter Supplier"
              className={classes.inputText}
              disabled={isEdit || isSubmitting}
            />
          </div>

          <div className={classes.formGroup}>
            <label>Supplier Name</label>
            <InputText
              value={formData.supplier_name}
              onChange={e => handleInputChange('supplier_name', e.target.value)}
              placeholder="Enter Supplier Name"
              className={classes.inputText}
              disabled={isEdit || isSubmitting}
            />
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Dosage Form</label>
            <Dropdown
              value={formData.dosage_form}
              options={dosageFormOptions}
              onChange={e => handleInputChange('dosage_form', e.value)}
              placeholder="Select Dosage Form"
              className={classes.dropdown}
              filter
              disabled={isSubmitting}
            />
          </div>

          <div className={classes.formGroup}>
            <label>Coating</label>
            {isCoatingDisabled ? (
              <InputText
                value="N/A"
                className={classes.inputText}
                disabled={true}
                style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
              />
            ) : (
              <Dropdown
                value={formData.coating}
                options={coatingOptions}
                onChange={e => handleInputChange('coating', e.value)}
                placeholder="Select Coating"
                className={classes.dropdown}
                filter
                disabled={isSubmitting}
                showClear
                valueTemplate={option => {
                  if (option === null) return 'N/A';
                  return option.label;
                }}
              />
            )}
            {isCoatingDisabled && (
              <small className="p-text-muted">
                Coating is automatically set to N/A when Dosage Form is "Any"
              </small>
            )}
          </div>
        </div>

        <div className={classes.formRow}>
          <div className={classes.formGroup}>
            <label>Product Color</label>
            <InputText
              value={formData.product_color}
              onChange={e => handleInputChange('product_color', e.target.value)}
              placeholder="Enter Product Color"
              className={classes.inputText}
              disabled={isSubmitting}
            />
          </div>
        </div>
      </div>

      <div className={classes.modalFooter}>
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={onHide}
          severity="danger"
          text
          disabled={isSubmitting}
        />

        <Button
          label={isSubmitting ? 'Saving...' : 'Save'}
          icon={isSubmitting ? 'pi pi-spinner pi-spin' : 'pi pi-check'}
          onClick={handleSubmit}
          className={classes.saveButton}
          disabled={isSubmitting}
        />
      </div>
    </Dialog>
  );
};

export default AddRowModalDrugReferenceFgNdcToBulkNdc;
